/**
 * P2T1 · admin-web 实时（原生 WebSocket·禁引 socket.io / 重型库）。
 *
 * 登录后连 /ws?token=<access_jwt> → 按当前用户角色订阅频道（镜像后端 _ROLE_CHANNEL_MAP）
 * → 收事件 → invalidateQueries 失效对应模块（如 complaint.raised → 投诉列表 + 首页铃铛聚合）。
 * 断线重连（指数退避）+ ping keepalive(30s) + 登出/换 token 断连重连。
 * base 不硬编码：从 VITE_API_BASE_URL 或当前 origin 派生。
 */
import { useEffect } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/authStore";
// ═ COMPANY-STRIP BEGIN ═ COMPANY-CUT-1 · 订阅版新订单提醒（SAAS-2）
import { ORDER_CREATED_EVENT } from "@/services/subscription";
// ═ COMPANY-STRIP END ═

// 角色 → 可订阅频道（镜像后端 realtime/channels.py _ROLE_CHANNEL_MAP）
const ROLE_CHANNELS: Record<string, string[]> = {
  owner: ["op_workbench", "supervisor", "dispatch", "driver"],
  tech_admin: ["op_workbench", "supervisor", "dispatch", "driver"],
  op_manager: ["op_workbench", "supervisor", "dispatch"],
  op_supervisor: ["op_workbench", "supervisor", "dispatch"],
  op_assistant: ["op_workbench", "dispatch"],
  customer_manager: ["op_workbench"],
  finance: ["op_workbench", "supervisor"],
  driver: ["driver"],
};

function channelsForUser(): string[] {
  const u = useAuthStore.getState().user;
  const roles: string[] = (u?.roleCodes as string[] | undefined) ?? (u?.role ? [u.role] : []);
  const set = new Set<string>();
  roles.forEach((r) => (ROLE_CHANNELS[r] ?? []).forEach((c) => set.add(c)));
  return [...set];
}

function wsUrl(token: string): string {
  const base = import.meta.env.VITE_API_BASE_URL as string | undefined;
  let host: string;
  let secure: boolean;
  if (base && /^https?:\/\//.test(base)) {
    const u = new URL(base);
    host = u.host;
    secure = u.protocol === "https:";
  } else {
    host = window.location.host;
    secure = window.location.protocol === "https:";
  }
  return `${secure ? "wss" : "ws"}://${host}/ws?token=${encodeURIComponent(token)}`;
}

function handleEvent(qc: QueryClient, msg: { type?: string; data?: unknown }) {
  const t = msg?.type;
  if (!t || t === "ready" || t === "ack" || t === "pong") return;
  // ═ COMPANY-STRIP BEGIN ═
  // SAAS-2 · 新订单提醒：后端只给订阅版租户推 order.created → 转成 window 事件，由顶栏「新订单提醒」（NewOrderAlert）决定弹不弹
  if (t === "order.created") window.dispatchEvent(new CustomEvent(ORDER_CREATED_EVENT, { detail: msg.data }));
  // ═ COMPANY-STRIP END ═
  // 任何业务事件 → 失效首页看板（铃铛红点聚合自 /home）
  qc.invalidateQueries({ queryKey: ["home"] });
  // QR1 闭环：投诉实时推 OP → 失效投诉列表（OpWorkbench / ComplaintsPage 即刷）
  if (t === "complaint.raised") qc.invalidateQueries({ queryKey: ["complaints"] });
  // 调度/打卡事件 → 失效调度视图
  if (t === "driver.checkpoint" || t.startsWith("dispatch.")) qc.invalidateQueries({ queryKey: ["dispatch"] });
}

/** 渲染于 providers 内的副作用组件（不渲染 UI）·维持登录态下的单 WS 连接。 */
export function RealtimeBridge() {
  const qc = useQueryClient();

  useEffect(() => {
    let ws: WebSocket | null = null;
    let pingTimer: ReturnType<typeof setInterval> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let disposed = false;

    const clearTimers = () => {
      if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    };

    const connect = () => {
      if (disposed) return;
      const token = useAuthStore.getState().accessToken;
      if (!token) return; // 未登录·不连（登录后由 store 订阅触发）
      try {
        ws = new WebSocket(wsUrl(token));
      } catch {
        scheduleReconnect();
        return;
      }
      ws.onopen = () => {
        attempt = 0;
        channelsForUser().forEach((ch) => ws?.send(JSON.stringify({ action: "subscribe", channel: ch })));
        pingTimer = setInterval(() => {
          if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ action: "ping" }));
        }, 30_000);
      };
      ws.onmessage = (e) => {
        let msg: { type?: string; data?: unknown };
        try { msg = JSON.parse(e.data); } catch { return; }
        handleEvent(qc, msg);
      };
      ws.onclose = () => {
        clearTimers();
        if (!disposed && useAuthStore.getState().accessToken) scheduleReconnect();
      };
      ws.onerror = () => { try { ws?.close(); } catch { /* ignore */ } };
    };

    const scheduleReconnect = () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      const delay = Math.min(30_000, 1000 * 2 ** attempt);
      attempt += 1;
      reconnectTimer = setTimeout(connect, delay);
    };

    const reconnectFresh = () => {
      clearTimers();
      attempt = 0;
      try { ws?.close(); } catch { /* ignore */ }
      ws = null;
      connect();
    };

    connect();
    // 登录/登出/换 token → 断旧连·按新 token 重连（plain zustand subscribe·无 selector 中间件）
    const unsub = useAuthStore.subscribe((state, prev) => {
      if (state.accessToken !== prev.accessToken) reconnectFresh();
    });

    return () => {
      disposed = true;
      clearTimers();
      try { ws?.close(); } catch { /* ignore */ }
      unsub();
    };
  }, [qc]);

  return null;
}
